/**
 * CompletionLog Service
 * Manages append-only event log for all goal completions and state changes
 */

import { v4 as uuidv4 } from 'uuid';
import { storage } from './StorageAdapter';
import { EventLogEntry, EventType } from '../../models/EventLogEntry';
import { CompletionEntry } from '../../models/CompletionEntry';
import { getTodayDateString } from '../../utils/dateUtils';

// Storage keys
const EVENT_LOG_KEY = 'event_log';
const DEVICE_ID_KEY = 'device_id';

/**
 * Get or create device ID for sync deduplication
 */
let cachedDeviceId: string | null = null;

const getDeviceId = async (): Promise<string> => {
  if (cachedDeviceId) {
    return cachedDeviceId;
  }

  let deviceId = await storage.getString(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = uuidv4();
    await storage.setString(DEVICE_ID_KEY, deviceId);
  }

  cachedDeviceId = deviceId;
  return deviceId;
};

/**
 * Append an event to the log (immutable, never deleted)
 */
export const appendEvent = async (
  accountId: string,
  type: EventType,
  payload: Record<string, any>
): Promise<EventLogEntry> => {
  const event: EventLogEntry = {
    id: uuidv4(),
    timestamp: Date.now(),
    accountId,
    type,
    payload,
    deviceId: await getDeviceId(),
    synced: false,
  };

  // Get existing log
  const logJson = await storage.getString(EVENT_LOG_KEY);
  const log: EventLogEntry[] = logJson ? JSON.parse(logJson) : [];

  // Append new event
  log.push(event);

  // Save back to storage
  await storage.setString(EVENT_LOG_KEY, JSON.stringify(log));

  return event;
};

/**
 * Get all events (for sync or export)
 */
export const getAllEvents = async (): Promise<EventLogEntry[]> => {
  const logJson = await storage.getString(EVENT_LOG_KEY);
  return logJson ? JSON.parse(logJson) : [];
};

/**
 * Get events for a specific account
 */
export const getAccountEvents = async (accountId: string): Promise<EventLogEntry[]> => {
  const allEvents = await getAllEvents();
  return allEvents.filter(event => event.accountId === accountId);
};

/**
 * Get events since a specific timestamp (for sync)
 */
export const getEventsSinceTimestamp = async (timestamp: number): Promise<EventLogEntry[]> => {
  const allEvents = await getAllEvents();
  return allEvents.filter(event => event.timestamp > timestamp);
};

/**
 * Replay events to build current state view for completions
 * Returns a map of date -> CompletionEntry
 */
export const replayCompletionEvents = async (
  accountId: string,
  goalId: string
): Promise<Map<string, CompletionEntry>> => {
  const events = await getAccountEvents(accountId);
  const completionMap = new Map<string, CompletionEntry>();

  for (const event of events) {
    if (
      event.type === 'GOAL_COMPLETED' ||
      event.type === 'GOAL_UNCOMPLETED'
    ) {
      const { goalId: eventGoalId, date, completed } = event.payload;

      if (eventGoalId === goalId) {
        // Create or update completion entry for this date
        const existing = completionMap.get(date);

        if (!existing || event.timestamp > existing.timestamp) {
          // Last-write-wins based on timestamp
          completionMap.set(date, {
            id: event.id,
            accountId,
            goalId: eventGoalId,
            date,
            completed: event.type === 'GOAL_COMPLETED',
            timestamp: event.timestamp,
            syncStatus: event.synced ? 'synced' : 'local',
          });
        }
      }
    }
  }

  return completionMap;
};

/**
 * Get completions for a specific date range
 */
export const getCompletionsForDateRange = async (
  accountId: string,
  goalId: string,
  dates: string[]
): Promise<CompletionEntry[]> => {
  const completionMap = await replayCompletionEvents(accountId, goalId);
  const results: CompletionEntry[] = [];

  for (const date of dates) {
    const completion = completionMap.get(date);
    if (completion) {
      results.push(completion);
    } else {
      // No entry = not completed
      results.push({
        id: uuidv4(),
        accountId,
        goalId,
        date,
        completed: false,
        timestamp: 0,
        syncStatus: 'local',
      });
    }
  }

  return results;
};

/**
 * Mark goal as completed or uncompleted for a specific date
 */
export const markGoalComplete = async (
  accountId: string,
  goalId: string,
  date: string,
  completed: boolean
): Promise<void> => {
  await appendEvent(
    accountId,
    completed ? 'GOAL_COMPLETED' : 'GOAL_UNCOMPLETED',
    {
      goalId,
      date,
      completed,
    }
  );
};

/**
 * Clear all event log data (for testing or reset)
 */
export const clearEventLog = async (): Promise<void> => {
  await storage.delete(EVENT_LOG_KEY);
};
